import { visibleSchedulingWarnings } from './schedulingWarnings';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useGetAssessmentSchedulingDefaultsQuery,
  useScheduleAssessmentMeetingMutation,
  type ParticipantSource,
} from '@/redux/internal-services/assessmentsApi';
import { useGetConfigurationQuery } from '@/redux/internal-services/onboardingApi';
import { Button } from '@/components/UI/Button';
import { CustomDatePicker, CustomTimePicker } from '@/components/UI/DateTimePickers';
import ParticipantPicker, { type ParticipantRow } from '@/components/Meetings/ParticipantPicker';


// Values match app.domain.reminders.recurrence.parse_offset's accepted format ("-1d", "-2h",
// "30m"). Kept local to this form rather than reusing MeetingForm's minutes-based
// REMINDER_OPTIONS, since assessment reminders are sent to the backend as offset strings.
//
// VALUES only — the labels are derived through formatOffsetLabel so there is one place that turns
// an offset into words. They previously carried hardcoded English labels, which meant a Spanish
// user saw "1 day before", and a tenant-configured offset outside this list took a separate
// (also English) code path.
const REMINDER_PRESETS = ['-15m', '-30m', '-1h', '-2h', '-1d'] as const;

/** Short label explaining why an auto-resolved participant is on the list. `owner_promoted` says
 *  so explicitly: that row is on the invite because the property has no primary contact set, and
 *  a Sales Agent should see that rather than assume someone chose the owner.
 *
 *  A `Record<ParticipantSource, ...>` rather than a switch with a default, ON PURPOSE: this is UI
 *  copy over a CODE-owned enum (unlike a tenant-configurable status code, which must never be
 *  hardcoded in the frontend), so the mapping belongs here — but the compiler now refuses to
 *  build if the backend adds a source and this map does not cover it. The `default: ''` this
 *  replaces would instead have rendered an unexplained participant row: still listed, still
 *  removable, with no reason shown. Silent, and only noticed by whoever wondered why someone was
 *  on the invite. */
const PARTICIPANT_ORIGIN_KEYS: Record<ParticipantSource, { key: string; fallback: string }> = {
  sales_agent: { key: 'assessments.originSalesAgent', fallback: 'Sales Agent' },
  actor: { key: 'assessments.originScheduler', fallback: 'scheduling this' },
  primary_poc: { key: 'assessments.originPrimaryContact', fallback: 'primary contact' },
  owner_promoted: {
    key: 'assessments.originOwnerPromoted',
    fallback: 'owner — no primary contact set',
  },
};

const participantOriginLabel = (
  source: ParticipantSource,
  backendLabel: string | null | undefined,
  t: TFunction,
): string => {
  // A backend-supplied label wins: it is the tenant's OWN name for a role, which this bundle
  // cannot know. A tenant that renamed "Sales Agent" to "Estimator" would otherwise read a name
  // they do not use — the same reason WorkflowService._tenant_role_labels exists for gate and
  // approval messages.
  if (backendLabel) return backendLabel;
  // An unknown source can still arrive at RUNTIME from a newer backend than this bundle, which
  // the compile-time check cannot catch — fall back to the raw source rather than a blank label,
  // so the row is at least traceable to a rule.
  const entry = PARTICIPANT_ORIGIN_KEYS[source];
  return entry ? t(entry.key, entry.fallback) : source;
};

/** Renders an offset string ("-1d", "-30m") as localized words.
 *
 *  Handles ANY offset the backend accepts, not just the presets: the chips are seeded from the
 *  tenant's `reminders.default_pre_due_offsets`, which a tenant admin can set to anything
 *  parse_offset understands. Singular/plural are separate keys rather than i18next plural
 *  suffixes because nothing else in this project's locale files uses `_one`/`_other`, and
 *  introducing that convention here alone would be a trap for the next person. Falls back to the
 *  raw offset when unparseable — showing "-3w" is more honest than showing nothing. */
const formatOffsetLabel = (offset: string, t: TFunction): string => {
  const m = /^-?(\d+)(m|h|d)$/.exec(offset.trim());
  if (!m) return offset;
  const count = Number(m[1]);
  const unit = m[2];
  const one = count === 1;
  if (unit === 'm') {
    return one
      ? t('assessments.reminderOffsetMinute', '{{count}} minute before', { count })
      : t('assessments.reminderOffsetMinutes', '{{count}} minutes before', { count });
  }
  if (unit === 'h') {
    return one
      ? t('assessments.reminderOffsetHour', '{{count}} hour before', { count })
      : t('assessments.reminderOffsetHours', '{{count}} hours before', { count });
  }
  return one
    ? t('assessments.reminderOffsetDay', '{{count}} day before', { count })
    : t('assessments.reminderOffsetDays', '{{count}} days before', { count });
};

interface AssessmentScheduleFormProps {
  engagementId: string;
  onScheduled?: () => void;
  /** Shows a Cancel button next to Submit when provided (the /assessments page's own toggle
   *  button already serves as its cancel, so it doesn't pass this). */
  onCancel?: () => void;
}

// EST-FEAT-01 — schedules a Lead assessment for an Engagement. Defaults (Sales Agent +
// Property POC participants, property-address location, tenant-configured duration) are all
// resolved server-side (see AssessmentSchedulingService) — this form only collects the
// start/end time, reminders and participants, mirroring MeetingForm.tsx's structure/styling.
//
// No agenda field here on purpose: the Agenda textarea was deliberately removed (commit 69f1ae8,
// a surgical 14-line deletion of just that section). The BACKEND still accepts `agenda`
// (ScheduleAssessmentRequest.agenda -> AssessmentSchedulingService, and the
// meeting_schedule_for_assessment governance action reads it out of input_values), so the
// business module's declared `agenda` input is real — it is simply not collected by this form.
// The leftover `agenda` state and submit line that survived that deletion are gone too; they only
// ever sent undefined.
const AssessmentScheduleForm: React.FC<AssessmentScheduleFormProps> = ({ engagementId, onScheduled, onCancel }) => {
  const { t } = useTranslation();
  const [scheduleAssessment, { isLoading }] = useScheduleAssessmentMeetingMutation();

  // Tenant-configured defaults — see AssessmentSchedulingService (default_duration_minutes)
  // and ReminderService.register_auto_reminders (default_pre_due_offsets). Both are read live
  // rather than hardcoded so this form always reflects what the backend will actually do.
  const { data: reminderCfgResp } = useGetConfigurationQuery('reminders');
  // EST-FR-01-002's resolved defaults, from the same backend resolver scheduling itself uses —
  // so the rows seeded below are exactly who would be invited if this form sent nothing.
  const { data: schedulingDefaults } = useGetAssessmentSchedulingDefaultsQuery(engagementId);
  const reminderCfg = reminderCfgResp?.data?.effective_value as
    | { default_pre_due_offsets?: string[] }
    | undefined;
  // NO fallback values here, deliberately (web-app/CLAUDE.md, "Backend-Driven UI — no frontend
  // fallbacks"). These previously defaulted to `60` and `['-1d','-1h']`, which duplicated the
  // catalogue's own defaults in a third place and — worse — meant a failed config load silently
  // showed the agent two reminders the tenant never configured, and then scheduled them. Undefined
  // now means "not known yet", and the UI says nothing rather than inventing a number.
  const defaultReminderOffsets = reminderCfg?.default_pre_due_offsets;

  const now = new Date();
  const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
  // BOTH halves are LOCAL. `toISOString()` yields the UTC date, and pairing that with a local
  // clock time put the two a day apart whenever the local and UTC dates differ — a Sales Agent in
  // PDT opening this at 17:00 was defaulted to 17:00 TOMORROW, and one in IST at 02:00 to 02:00
  // YESTERDAY. Both are then submitted as a real ISO instant, so confirming without touching the
  // date booked the wrong day. (MeetingForm.tsx still formats its own defaults the old way.)
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fmtT = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

  const [startDate, setStartDate] = useState(fmt(now));
  const [startTime, setStartTime] = useState(fmtT(now));
  const [endDate, setEndDate] = useState(fmt(oneHourLater));
  const [endTime, setEndTime] = useState(fmtT(oneHourLater));
  const [participants, setParticipants] = useState<ParticipantRow[]>([]);
  const visibleWarnings = visibleSchedulingWarnings(schedulingDefaults?.warnings ?? [], participants);
  const [reminderOffsets, setReminderOffsets] = useState<string[]>([]);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [showCustomReminder, setShowCustomReminder] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [customUnit, setCustomUnit] = useState<'m' | 'h' | 'd'>('h');
  const [error, setError] = useState('');

  // Seed reminder chips from the tenant's configured default exactly once, when the config
  // finishes loading — not on every render, so it doesn't clobber the agent's own edits.
  // Seed the participant rows from the resolved defaults, exactly once, so they appear as
  // ordinary removable rows in the same picker used for manual additions — rather than as a
  // separate read-only list the user can look at but not act on. Removal sticks because the
  // form posts the FINAL list back as `participants`, which suppresses server-side resolution.
  const participantsSeeded = useRef(false);
  useEffect(() => {
    if (participantsSeeded.current || !schedulingDefaults) return;
    participantsSeeded.current = true;
    setParticipants(schedulingDefaults.participants.map((r, i) => ({
      key: `default-${r.participant.user_id || r.participant.contact_id || i}`,
      type: r.participant.participant_type,
      user_id: r.participant.user_id || undefined,
      contact_id: r.participant.contact_id || undefined,
      name: r.participant.name || '',
      email: r.participant.email || '',
      phone: r.participant.phone || '',
      origin: participantOriginLabel(r.source, r.source_label, t),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingDefaults]);

  const remindersSeeded = useRef(false);
  useEffect(() => {
    // Only seed from a value the backend actually sent. A loaded-but-empty config means this
    // tenant wants no default reminders, which must read as no chips — not as the two this form
    // used to assume.
    if (!remindersSeeded.current && Array.isArray(defaultReminderOffsets)) {
      setReminderOffsets(defaultReminderOffsets);
      remindersSeeded.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderCfgResp]);

  const addReminderOffset = (value: string) => {
    setReminderOffsets((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setShowAddReminder(false);
  };
  const addCustomReminderOffset = () => {
    const amount = Number(customAmount);
    if (!Number.isInteger(amount) || amount <= 0) return;
    addReminderOffset(`-${amount}${customUnit}`);
    setCustomAmount('');
    setCustomUnit('h');
    setShowCustomReminder(false);
  };
  const removeReminderOffset = (value: string) => {
    setReminderOffsets((prev) => prev.filter((o) => o !== value));
  };

  // When the user moves the start, shift the end forward by the same duration so the meeting
  // length is preserved. Called by both the date and time pickers for start.
  const shiftEndToKeepDuration = (newDateStr: string, newTimeStr: string) => {
    const oldStart = new Date(`${startDate}T${startTime}`);
    const oldEnd   = new Date(`${endDate}T${endTime}`);
    const duration = Number.isFinite(oldEnd.getTime() - oldStart.getTime())
      ? oldEnd.getTime() - oldStart.getTime()
      : 60 * 60 * 1000; // fallback 1 h if either date is invalid
    const newStart = new Date(`${newDateStr}T${newTimeStr}`);
    const newEnd   = new Date(newStart.getTime() + duration);
    setEndDate(fmt(newEnd));
    setEndTime(fmtT(newEnd));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !startTime) {
      setError(t('assessments.formRequiredError', 'Start date and time are required.'));
      return;
    }
    const start = new Date(`${startDate}T${startTime}`);
    const end   = new Date(`${endDate}T${endTime}`);
    if (!(end > start)) {
      setError(t('assessments.endBeforeStart', 'End time must be after the start time.'));
      return;
    }
    setError('');
    try {
      await scheduleAssessment({
        engagement_id: engagementId,
        start_time: new Date(`${startDate}T${startTime}`).toISOString(),
        end_time: new Date(`${endDate}T${endTime}`).toISOString(),
        // The FINAL list, not additions: these rows were seeded from the backend's own defaults
        // and the user may have removed some. Sending `additional_participants` here would have
        // the server re-resolve and re-add exactly what they just deleted.
        participants: participants.map((p) => ({
          user_id: p.user_id || undefined,
          contact_id: p.contact_id || undefined,
          participant_type: p.type,
          name: p.name || undefined,
          email: p.email || undefined,
          phone: p.phone || undefined,
        })),
        reminder_offsets: reminderOffsets,
      }).unwrap();
      onScheduled?.();
    } catch (err: unknown) {
      const msg = (err as { data?: { message?: string } })?.data?.message
        || t('assessments.scheduleFailed', 'Failed to schedule the assessment.');
      setError(msg);
    }
  };

  return (
<form onSubmit={handleSubmit} className="w-full">
  {error && (
    <p className="mb-2 rounded-md bg-danger/10 px-3 py-1.5 text-xs text-danger-text">
      {error}
    </p>
  )}

  <div className="space-y-1">
    {/* Start / End Time */}
    <section>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-ui-text mb-0.5">
            {t('assessments.startTime', 'Start Time')}
          </label>
          <div className="space-y-0.5">
            <CustomDatePicker value={startDate} onChange={(d) => { setStartDate(d); shiftEndToKeepDuration(d, startTime); }} />
            <CustomTimePicker value={startTime} onChange={(t) => { setStartTime(t); shiftEndToKeepDuration(startDate, t); }} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-ui-text mb-0.5">
            {t('assessments.endTime', 'End Time')}
          </label>
          <div className="space-y-0.5">
            <CustomDatePicker value={endDate} onChange={setEndDate} />
            <CustomTimePicker value={endTime} onChange={setEndTime} />
          </div>
        </div>
      </div>
    </section>

    {/* Reminders */}
    <section>
      <div className="flex items-center justify-between">
        <label className="block text-xs font-medium text-ui-text">
          {t('assessments.reminders', 'Reminders')}
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowAddReminder((v) => !v)}
            className="rounded-md border border-dashed border-ui-border px-2 py-0.5 text-xs font-semibold text-ui-text2 hover:bg-ui-hover"
          >
            + {t('assessments.addReminder', 'Add reminder')}
          </button>

          {showAddReminder && (
            <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-ui-border bg-ui-card py-1 shadow-md">
              {REMINDER_PRESETS.filter((value) => !reminderOffsets.includes(value)).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => addReminderOffset(value)}
                  className="block w-full px-3 py-1.5 text-left text-xs text-ui-text hover:bg-accent-soft/30 transition-colors"
                >
                  {formatOffsetLabel(value, t)}
                </button>
              ))}
              {REMINDER_PRESETS.every((value) => reminderOffsets.includes(value)) && (
                <p className="px-3 py-1.5 text-xs text-ui-text3">
                  {t('assessments.allRemindersAdded', 'All presets added')}
                </p>
              )}

              <div className="my-1 border-t border-ui-border" />

              {!showCustomReminder ? (
                <button
                  type="button"
                  onClick={() => setShowCustomReminder(true)}
                  className="block w-full px-3 py-1.5 text-left text-xs font-medium text-accent-text hover:bg-accent-soft/30 transition-colors"
                >
                  {t('assessments.customReminder', 'Custom…')}
                </button>
              ) : (
                <div className="px-2 py-1.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder={t('assessments.customReminderAmount', 'Amount')}
                      className="w-16 rounded-md border border-ui-border bg-ui-input px-1.5 py-1 text-xs text-ui-text focus:outline-none focus:ring-1 focus:ring-ui-ring/30"
                    />
                    <select
                      value={customUnit}
                      onChange={(e) => setCustomUnit(e.target.value as 'm' | 'h' | 'd')}
                      className="flex-1 rounded-md border border-ui-border bg-ui-input px-1.5 py-1 text-xs text-ui-text focus:outline-none focus:ring-1 focus:ring-ui-ring/30"
                    >
                      <option value="m">{t('assessments.unitMinutes', 'min')}</option>
                      <option value="h">{t('assessments.unitHours', 'hrs')}</option>
                      <option value="d">{t('assessments.unitDays', 'days')}</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={addCustomReminderOffset}
                      disabled={!customAmount || Number(customAmount) <= 0}
                      className="flex-1 rounded-md btn-sheen px-2 py-1 text-xs font-semibold text-on-primary disabled:opacity-50 transition-colors"
                    >
                      {t('assessments.customReminderAdd', 'Add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCustomReminder(false); setCustomAmount(''); }}
                      className="text-xs text-ui-text3 hover:text-ui-text"
                    >
                      {t('workflow.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-0.5 flex flex-wrap gap-1.5">
        {reminderOffsets.length === 0 && (
          <span className="text-xs italic text-ui-text3">
            {t('assessments.noReminders', 'No reminders — click "Add reminder" to schedule one.')}
          </span>
        )}
        {reminderOffsets.map((offset) => (
          <span
            key={offset}
            className="inline-flex items-center gap-1 rounded-full bg-accent-soft/40 px-2.5 py-1 text-xs font-medium text-accent-text"
          >
            {formatOffsetLabel(offset, t)}
            <button
              type="button"
              onClick={() => removeReminderOffset(offset)}
              className="text-accent-text/60 hover:text-accent-text"
              aria-label={t('assessments.removeReminder', 'Remove reminder')}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    </section>

    {/* Participants */}
    <section>
      <div className="w-full min-w-0">
        <ParticipantPicker
          participants={participants}
          onChange={setParticipants}
        />
      </div>

      {visibleWarnings.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {visibleWarnings.map((w) => (
            <p key={w} className="text-xs leading-4 text-warning-text">{w}</p>
          ))}
        </div>
      )}
    </section>

    {/* Actions */}
    <div className="flex items-center justify-end gap-2">
      {onCancel && (
        <Button
          type="button"
          variant="ghost"
          disabled={isLoading}
          onClick={onCancel}
          className="h-6 md:h-7 min-h-0 w-auto px-3 py-1 text-xs"
        >
          {t('workflow.cancel', 'Cancel')}
        </Button>
      )}

      <Button
        type="submit"
        disabled={isLoading}
        className="h-6 md:h-7 min-h-0 w-auto px-3 py-1 text-xs"
      >
        {isLoading
          ? t('assessments.scheduling', 'Confirming…')
          : t('assessments.scheduleAssessment')}
      </Button>
    </div>
  </div>
</form>
  );
};

export default AssessmentScheduleForm;
